import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import { FileAttachmentDetails, QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import store from "../../Store";
import { deleteFile } from "../../api";
import { v4 as uuidv4 } from "uuid";
import i18next from "i18next";
import { appInfo } from "../../authConfig";

// Server-side file structure
export type ServerSideFile = {
    name: string;
    type: string;
    size: number;
    content?: string;
    content_path?: string;
};

// Support both File instances (user uploads) and server-side objects (from backend)
export type Attachment = {
    file: File | ServerSideFile;
    preview?: string;
    id: string;
};

// Helper to extract file properties from either File instance or server-side object
export function getFileProperties(attachment: Attachment) {
    if (attachment.file instanceof File) {
        return {
            name: attachment.file.name,
            type: attachment.file.type,
            size: attachment.file.size
        };
    }
    const serverFile = attachment.file as ServerSideFile;
    return {
        name: serverFile.name,
        type: serverFile.type,
        size: serverFile.size
    };
}

export const enum TriggerTypeAPI {
    FILE_UPLOAD = "FILE_UPLOAD",
    FILE_DELETE = "FILE_DELETE",
    FILE_UPLOAD_DETACH = "FILE_UPLOAD_DETACH"
}

export function FileAttachments() {
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const dispatch = useDispatch();
    const MAX_FILES = Number(appInfo?.file_attachment?.attachment_max_limit ?? 10);
    const DEFAULT_MAX_FILE_SIZE_BYTES = 1024 * 1024; // 1MB
    const rawMaxFileSize = Number(appInfo?.file_attachment?.attachment_max_size ?? DEFAULT_MAX_FILE_SIZE_BYTES);
    const MAX_FILE_SIZE_BYTES = Number.isFinite(rawMaxFileSize) && rawMaxFileSize > 0 ? rawMaxFileSize : DEFAULT_MAX_FILE_SIZE_BYTES;

    useEffect(() => {
        dispatch(QuestionInputReducerAction.updateFileAttachments([...attachments]));
    }, [attachments]);

    const addFiles = (files: FileList | File[]) => {
        const previousAttachments = store.getState().questionInput.fileAttachments;
        // same file of same name & same size is considered as same file reuploaded or as duplicate upload.
        const alreadyAdded = new Set(previousAttachments.map(att => att.file.name + att.file.size + att.file.type));
        // Step 1: Remove duplicates
        let uniqueFiles = Array.from(files).filter(file => !alreadyAdded.has(file.name + file.size + file.type));
        const duplicatesSkipped = files.length - uniqueFiles.length;

        // Step 2: Remove 0KB files
        const zeroSizeFiles = uniqueFiles.filter(file => file.size === 0);
        if (zeroSizeFiles.length > 0) {
            uniqueFiles = uniqueFiles.filter(file => file.size > 0);
        }

        // Step 3: Remove files exceeding size limit
        const filesExceedingLimit = uniqueFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
        if (filesExceedingLimit.length > 0) {
            uniqueFiles = uniqueFiles.filter(file => file.size <= MAX_FILE_SIZE_BYTES);
        }

        // Step 4: Enforce max file count after all filtering
        const availableSlots = MAX_FILES - previousAttachments.length;
        const filesToAdd = uniqueFiles.slice(0, availableSlots);
        const filesSkippedDueToLimit = Math.max(0, uniqueFiles.length - availableSlots);

        // Show appropriate snackbar messages based on file filtering results
        let errorExist = false;
        let errorMessage = "";
        const errorTypes: ("success" | "error" | "warning" | "info")[] = [];
        if (zeroSizeFiles.length > 0) {
            errorExist = true;
            errorMessage = errorMessage + i18next.t("fileAttachment.zeroSizeFilesSkipped") + "<br>";
            errorTypes.push("warning");
        }
        if (filesExceedingLimit.length > 0) {
            errorExist = true;
            errorMessage = errorMessage + i18next.t("fileAttachment.fileSizeExceeded") + "<br>";
            errorTypes.push("error");
        }
        if (duplicatesSkipped > 0) {
            errorExist = true;
            errorMessage = errorMessage + i18next.t("fileAttachment.duplicateFilesSkipped") + "<br>";
            errorTypes.push("warning");
        }
        if (availableSlots === 0 || filesSkippedDueToLimit > 0) {
            errorExist = true;
            errorMessage = errorMessage + i18next.t("fileAttachment.maxFilesReached") + "<br>";
            errorTypes.push("warning");
        }
        if (errorExist) {
            showSnackbar(errorMessage, getHighestPrioritySeverity(errorTypes));
        }

        const newAttachments: Attachment[] = [
            ...previousAttachments,
            ...filesToAdd.map(f => ({
                file: f,
                preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
                id: String(uuidv4())
            }))
        ];
        setAttachments(newAttachments);
        if (filesToAdd.length > 0) {
            dispatch(QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({ triggerActive: true, triggerAPI: TriggerTypeAPI.FILE_UPLOAD }));
        }
    };

    const removeAttachment = async (fileId: string | string[], triggerType?: TriggerTypeAPI | null) => {
        if (!triggerType) {
            dispatch(QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({ triggerActive: true, triggerAPI: TriggerTypeAPI.FILE_DELETE }));
        }
        const serverSideFileAttachmentsIds: Array<FileAttachmentDetails> = store.getState().questionInput.serverSideFileAttachmentsIds;
        const removeAttachment = !triggerType
            ? serverSideFileAttachmentsIds.filter(att => (typeof fileId === "string" ? att.frontEndId === fileId : fileId.includes(att.frontEndId)))
            : store.getState().questionInput.fileAttachments.filter(att => (typeof fileId === "string" ? att.id === fileId : fileId.includes(att.id)));
        if (removeAttachment && removeAttachment.length > 0) {
            try {
                if (!triggerType) {
                    await deleteFile(removeAttachment);
                }
                // Revoke blob URLs before removing attachments to prevent memory leaks
                setAttachments(prev => {
                    const toRemove = prev.filter(att => (typeof fileId === "string" ? att.id === fileId : fileId.includes(att.id)));
                    toRemove.forEach(att => {
                        if (att.preview && att.preview.startsWith("blob:")) {
                            URL.revokeObjectURL(att.preview);
                        }
                    });
                    return prev.filter(att => (typeof fileId === "string" ? att.id !== fileId : !fileId.includes(att.id)));
                });
                const updatedServerSideIds: Array<FileAttachmentDetails> = serverSideFileAttachmentsIds.filter(att =>
                    typeof fileId === "string" ? att.frontEndId !== fileId : !fileId.includes(att.frontEndId)
                );
                dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds(updatedServerSideIds));
                dispatch(QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({ triggerActive: false, triggerAPI: null }));
            } catch (error) {
                console.error("File attachment error:", error);
                const errorMessage = error instanceof Error ? error.message : String(error);
                showSnackbar(errorMessage, "error");
                dispatch(QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({ triggerActive: false, triggerAPI: null }));
            }
        }
    };

    const clearAttachments = () => {
        // Revoke blob URLs before clearing attachments to prevent memory leaks
        setAttachments(prev => {
            prev.forEach(att => {
                if (att.preview && att.preview.startsWith("blob:")) {
                    URL.revokeObjectURL(att.preview);
                }
            });
            return [];
        });
    };

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity?: "success" | "error" | "warning" | "info";
    }>({
        open: false,
        message: "",
        severity: "info"
    });

    const showSnackbar = useCallback((message: string, severity: "success" | "error" | "warning" | "info" = "info") => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    }, []);

    const hideSnackbar = useCallback(() => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    }, []);

    const getHighestPrioritySeverity = (types: ("success" | "error" | "warning" | "info")[]): "success" | "error" | "warning" | "info" => {
        if (types.includes("error")) return "error";
        if (types.includes("warning")) return "warning";
        if (types.includes("info")) return "info";
        return "success";
    };

    const SnackbarComponent = (
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={hideSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Alert onClose={hideSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
                <div dangerouslySetInnerHTML={{ __html: snackbar.message }} />
            </Alert>
        </Snackbar>
    );

    return {
        attachments,
        addFiles,
        removeAttachment,
        clearAttachments,
        SnackbarComponent,
        showSnackbar
    };
}
